import { type MutableRefObject, useEffect, useRef } from 'react';

export default function useScroll<T extends HTMLElement>(props: {
  containerRef: MutableRefObject<T | null>;
  headerOffset: number;
  onScroll: (offset: number) => void | boolean;
  isScrollControlsVisible: boolean;
}) {
  const { containerRef, headerOffset, onScroll, isScrollControlsVisible } = props;

  // touch
  const positionRef = useRef({
    clientX: 0,
  });

  function onWheel(e: WheelEvent) {
    if (!containerRef.current || !isScrollControlsVisible) {
      return;
    }

    const element = containerRef.current;
    const currentTransform = element.style.transform;
    const matrix = new DOMMatrix(currentTransform);
    const currentTranslateX = matrix.m41;

    e.preventDefault();

    const { deltaX, deltaY } = e;

    let offset = 0;

    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);

    if (absX >= absY) {
      offset = deltaX;
    } else {
      offset = deltaY;
    }
    const translateX = currentTranslateX - offset;

    onScroll(translateX);
  }

  const getPosition = (e: TouchEvent) => {
    return e && e.touches && e.touches.length && e.touches[0];
  };

  function onOffset(offsetX: number) {
    const offset = headerOffset - offsetX;

    onScroll && onScroll(offset);
  }

  const onTouchMove = (e: TouchEvent) => {
    if (e.cancelable) {
      e.preventDefault();
    }

    const position = getPosition(e);
    if (!position) {
      return;
    }

    const { clientX } = positionRef.current;

    // 往右移动的距离
    const offsetX = position.clientX - clientX;

    onOffset(-offsetX);
  };

  const onTouchMoveEnd = () => {
    document.documentElement.removeEventListener('touchmove', onTouchMove);
    window.removeEventListener('touchend', onTouchMoveEnd);
  };

  const onTouchStart = (e: TouchEvent) => {
    const position = getPosition(e);
    if (!position) {
      return;
    }
    positionRef.current = {
      clientX: position.clientX,
    };

    document.documentElement.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchMoveEnd, { passive: false });
  };

  useEffect(() => {
    containerRef.current?.addEventListener('wheel', onWheel);
    containerRef.current?.addEventListener('touchstart', onTouchStart);

    return () => {
      containerRef.current?.removeEventListener('wheel', onWheel);
      containerRef.current?.removeEventListener('touchstart', onTouchStart);
    };
  }, [headerOffset, isScrollControlsVisible]);
}
